import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  TextField,
  Link as MuiLink,
  Stack,
  Chip,
  Divider,
  IconButton,
  Checkbox,
  FormControlLabel,
  GlobalStyles,
  Container,
} from "@mui/material";
import { styled } from "@mui/system";
import { Element, scroller } from "react-scroll";
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom";
import axiosInstance from "../axiosInstance";
import { API_ENDPOINTS } from "../config";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AssessmentIcon from "@mui/icons-material/Assessment";
import DescriptionIcon from "@mui/icons-material/Description";
import BarChartIcon from "@mui/icons-material/BarChart";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import VerifiedIcon from "@mui/icons-material/Verified";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import EmailIcon from "@mui/icons-material/Email";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import LanguageIcon from "@mui/icons-material/Language";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import SpeedIcon from "@mui/icons-material/Speed";
import GroupsIcon from "@mui/icons-material/Groups";

const une0077Logo = "/images/une-0077.png";
const une0078Logo = "/images/une-0078.png";
const une0079Logo = "/images/une-0079.png";
const une0080Logo = "/images/une-0080.png";

const COLORS = {
  orange: "#d37d36",
  orangeDark: "#b8662b",
  blue: "#1e4b81",
  blueDark: "#102840",
  blueMid: "#244f83",
  ink: "#0f2438",
  text: "#1d2f43",
  muted: "#64748b",
  soft: "#f6f8fb",
  softBlue: "#eef5ff",
  white: "#ffffff",
  border: "rgba(15,36,56,0.10)",
  borderStrong: "rgba(15,36,56,0.16)",
};

const standardLogos = [
  { name: "UNE 0077", description: "Gobierno del Dato", image: une0077Logo },
  { name: "UNE 0078", description: "Gestión del Dato", image: une0078Logo },
  { name: "UNE 0079", description: "Calidad del Dato", image: une0079Logo },
  { name: "UNE 0080", description: "Madurez Organizacional", image: une0080Logo },
];

const features = [
  {
    title: "Autoevaluación guiada",
    desc: "Cuestionarios estructurados por procesos para evaluar capacidad y madurez sin partir de cero.",
    icon: <AssessmentIcon />,
  },
  {
    title: "Diagnóstico visual",
    desc: "Resultados por ámbito, proceso y nivel para entender rápidamente dónde está la organización.",
    icon: <BarChartIcon />,
  },
  {
    title: "Brechas accionables",
    desc: "Identificación clara de desviaciones frente al nivel objetivo y principales puntos de mejora.",
    icon: <TrackChangesIcon />,
  },
  {
    title: "Hoja de ruta priorizada",
    desc: "Propuestas ordenadas por impacto para convertir la evaluación en un plan de mejora realista.",
    icon: <TaskAltIcon />,
  },
  {
    title: "Informes ejecutivos",
    desc: "Exportación de resultados y conclusiones para comités, responsables de datos y dirección.",
    icon: <DescriptionIcon />,
  },
  {
    title: "Enfoque UNE",
    desc: "Modelo orientado a gobierno, gestión, calidad del dato y madurez organizacional.",
    icon: <VerifiedIcon />,
  },
];

const plans = [
  {
    title: "Freemium",
    planType: "freemium",
    description: "Primera evaluación para validar el enfoque y conocer el estado inicial.",
    cta: "Empezar gratis",
    features: [
      { text: "Nivel de capacidad 2 para 5 procesos", included: true },
      { text: "Una autoevaluación", included: true },
      { text: "Resultados y propuestas de mejora", included: true },
      { text: "Evaluación de madurez organizacional", included: false },
      { text: "Descarga de resultados", included: false },
    ],
  },
  {
    title: "Premium",
    planType: "premium",
    description: "Diagnóstico ampliado para equipos que quieren evaluar procesos clave.",
    cta: "Elegir Premium",
    features: [
      { text: "Nivel de capacidad 2 para 9 procesos", included: true },
      { text: "Dos autoevaluaciones", included: true },
      { text: "Resultados y propuestas de mejora", included: true },
      { text: "Madurez organizacional nivel 2", included: true },
      { text: "Descarga de resultados", included: false },
    ],
  },
  {
    title: "Premium Pro",
    planType: "premiumPro",
    description: "Evaluación integral para organizaciones que necesitan una evaluación completa de todos los procesos.",
    cta: "Solicitar contacto",
    features: [
      { text: "Nivel de capacidad 5 para 22 procesos", included: true },
      { text: "Autoevaluaciones ilimitadas", included: true },
      { text: "Resultados y propuestas de mejora", included: true },
      { text: "Madurez organizacional nivel 5", included: true },
      { text: "Descarga de resultados", included: true },
    ],
  },
];

const contactItems = [
  { title: "Teléfono", value: "926 29 52 48", icon: <PhoneIcon /> },
  {
    title: "Ubicación",
    value: "Cam. Moledores, 13005 Ciudad Real",
    icon: <LocationOnIcon />,
  },
  { title: "Email", value: "dqpa@dqteam.es", icon: <EmailIcon /> },
];

const PageContainer = styled(Container)(({ theme }) => ({
  width: "100%",
  maxWidth: "1120px !important",
  marginLeft: "auto",
  marginRight: "auto",
  paddingLeft: "16px !important",
  paddingRight: "16px !important",
  boxSizing: "border-box",
  minWidth: 0,

  [theme.breakpoints.up("sm")]: {
    paddingLeft: "20px !important",
    paddingRight: "20px !important",
  },

  [theme.breakpoints.up("lg")]: {
    paddingLeft: "28px !important",
    paddingRight: "28px !important",
  },
}));

const GlassCard = styled(Card)({
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  borderRadius: 28,
  background: "rgba(255,255,255,0.88)",
  border: `1px solid ${COLORS.border}`,
  boxShadow: "0 22px 70px rgba(15,36,56,0.09)",
  backdropFilter: "blur(18px)",
  overflow: "hidden",
});

const CleanCard = styled(Card)({
  width: "100%",
  maxWidth: "100%",
  minWidth: 0,
  borderRadius: 26,
  backgroundColor: COLORS.white,
  border: `1px solid ${COLORS.border}`,
  boxShadow: "0 18px 45px rgba(15,36,56,0.055)",
  overflow: "hidden",
});

const textNoHyphenSx = {
  overflowWrap: "normal",
  wordBreak: "normal",
  hyphens: "none",
  WebkitHyphens: "none",
  MozHyphens: "none",
  msHyphens: "none",
};

const PrimaryButton = styled(Button)(({ theme }) => ({
  borderRadius: 14,
  padding: "10px 17px",
  minHeight: 42,
  fontWeight: 900,
  textTransform: "none",
  backgroundColor: COLORS.orange,
  color: COLORS.white,
  boxShadow: "0 16px 34px rgba(211,125,54,0.25)",
  transition: "all .22s ease",
  maxWidth: "100%",
  whiteSpace: "normal",
  lineHeight: 1.2,
  textAlign: "center",
  hyphens: "none",

  "&:hover": {
    backgroundColor: COLORS.orangeDark,
    boxShadow: "0 20px 42px rgba(211,125,54,0.31)",
    transform: "translateY(-1px)",
  },

  "&.Mui-disabled": {
    backgroundColor: COLORS.orange,
    color: COLORS.white,
    opacity: 0.68,
  },

  [theme.breakpoints.down("sm")]: {
    width: "100%",
    minHeight: 44,
    padding: "10px 14px",
    fontSize: "0.86rem",
  },
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  borderRadius: 14,
  padding: "10px 17px",
  minHeight: 42,
  fontWeight: 900,
  textTransform: "none",
  backgroundColor: COLORS.white,
  color: COLORS.blueDark,
  border: `1px solid ${COLORS.borderStrong}`,
  transition: "all .22s ease",
  maxWidth: "100%",
  whiteSpace: "normal",
  lineHeight: 1.2,
  textAlign: "center",
  hyphens: "none",

  "&:hover": {
    borderColor: "rgba(211,125,54,0.45)",
    backgroundColor: "rgba(211,125,54,0.06)",
    transform: "translateY(-1px)",
  },

  [theme.breakpoints.down("sm")]: {
    width: "100%",
    minHeight: 44,
    padding: "10px 14px",
    fontSize: "0.86rem",
  },
}));

const ResponsiveGrid = ({ children, columns, gap = { xs: 2.2, md: 4 }, sx = {} }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: columns,
      gap,
      width: "100%",
      maxWidth: "100%",
      minWidth: 0,
      overflow: "hidden",

      "& > *": {
        minWidth: 0,
        maxWidth: "100%",
        overflow: "hidden",
      },

      ...sx,
    }}
  >
    {children}
  </Box>
);

const SafeBox = ({ children, sx = {}, ...props }) => (
  <Box
    {...props}
    sx={{
      width: "100%",
      maxWidth: "100%",
      minWidth: 0,
      overflow: "hidden",
      ...textNoHyphenSx,
      ...sx,
    }}
  >
    {children}
  </Box>
);

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "16px",
    bgcolor: "#fff",
    "& fieldset": { borderColor: "rgba(15,36,56,0.14)" },
    "&:hover fieldset": { borderColor: COLORS.orange },
    "&.Mui-focused fieldset": { borderColor: COLORS.orange, borderWidth: 2 },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: COLORS.orange },
};

const IconBadge = ({ children, variant = "orange", size = 52 }) => (
  <Box
    sx={{
      width: size,
      height: size,
      borderRadius: "18px",
      display: "grid",
      placeItems: "center",
      color: variant === "blue" ? COLORS.blue : COLORS.orange,
      bgcolor:
        variant === "blue"
          ? "rgba(30,75,129,0.08)"
          : "rgba(211,125,54,0.10)",
      border:
        variant === "blue"
          ? "1px solid rgba(30,75,129,0.12)"
          : "1px solid rgba(211,125,54,0.18)",
      flexShrink: 0,
      "& svg": { fontSize: 25 },
    }}
  >
    {children}
  </Box>
);

const SectionHeader = ({ eyebrow, title, highlight, subtitle, align = "center" }) => (
  <Box
    sx={{
      textAlign: { xs: "left", md: align },
      width: "100%",
      maxWidth: align === "center" ? 820 : 680,
      mx: align === "center" ? { xs: 0, md: "auto" } : 0,
      mb: { xs: 3.5, md: 5.5 },
      minWidth: 0,
    }}
  >
    {eyebrow && (
      <Typography
        sx={{
          color: COLORS.orange,
          fontSize: "0.72rem",
          fontWeight: 950,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          mb: 1.1,
          ...textNoHyphenSx,
        }}
      >
        {eyebrow}
      </Typography>
    )}

    <Typography
      component="h2"
      sx={{
        fontSize: {
          xs: "clamp(1.45rem, 6vw, 1.85rem)",
          sm: "1.85rem",
          md: "2.25rem",
        },
        lineHeight: 1.12,
        letterSpacing: { xs: "-0.025em", md: "-0.045em" },
        fontWeight: 950,
        color: COLORS.blueDark,
        mb: 1.5,
        maxWidth: "100%",
        ...textNoHyphenSx,
      }}
    >
      {title}{" "}
      {highlight && (
        <Box component="span" sx={{ color: COLORS.orange, ...textNoHyphenSx }}>
          {highlight}
        </Box>
      )}
    </Typography>

    {subtitle && (
      <Typography
        sx={{
          color: COLORS.muted,
          fontSize: { xs: "0.86rem", md: "0.95rem" },
          lineHeight: 1.6,
          maxWidth: 760,
          mx: align === "center" ? { xs: 0, md: "auto" } : 0,
          ...textNoHyphenSx,
        }}
      >
        {subtitle}
      </Typography>
    )}
  </Box>
);

const MiniKpi = ({ value, label }) => (
  <Box
    sx={{
      p: { xs: 1, sm: 1.35 },
      borderRadius: "16px",
      bgcolor: "rgba(255,255,255,0.74)",
      border: "1px solid rgba(15,36,56,0.08)",
      minWidth: 0,
      overflow: "hidden",
    }}
  >
    <Typography
      sx={{
        color: COLORS.blueDark,
        fontSize: { xs: "0.95rem", sm: "1.28rem" },
        fontWeight: 950,
        lineHeight: 1,
        mb: 0.45,
        ...textNoHyphenSx,
      }}
    >
      {value}
    </Typography>

    <Typography
      sx={{
        color: COLORS.muted,
        fontSize: { xs: "0.58rem", sm: "0.72rem" },
        fontWeight: 800,
        lineHeight: 1.25,
        ...textNoHyphenSx,
      }}
    >
      {label}
    </Typography>
  </Box>
);

const DashboardPreview = () => {
  const areas = [
    { name: "Gobierno", value: 76, color: COLORS.blue },
    { name: "Gestión", value: 64, color: COLORS.orange },
    { name: "Calidad", value: 58, color: COLORS.blueMid },
  ];

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        maxWidth: { xs: "100%", sm: 480, md: 500 },
        mx: "auto",
        minWidth: 0,
        overflow: "visible",
        pt: 4,
      }}
    >
      <GlassCard
        sx={{
          position: "absolute",
          top: 0,
          right: { xs: 6, sm: 8, md: 10 },
          zIndex: 2,
          display: { xs: "none", sm: "block" },
          width: "auto",
          maxWidth: "calc(100% - 16px)",
          px: 1.35,
          py: 0.85,
          borderRadius: "16px",
          overflow: "visible",
        }}
      >
        <Stack direction="row" spacing={0.9} alignItems="center" sx={{ minWidth: 0 }}>
          <SpeedIcon sx={{ color: COLORS.orange, fontSize: 18, flexShrink: 0 }} />

          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                color: COLORS.blueDark,
                fontWeight: 950,
                fontSize: "0.75rem",
                lineHeight: 1,
                ...textNoHyphenSx,
              }}
              noWrap
            >
              Resultado inmediato
            </Typography>

            <Typography
              sx={{
                color: COLORS.muted,
                fontSize: "0.63rem",
                fontWeight: 700,
                ...textNoHyphenSx,
              }}
              noWrap
            >
              evaluación y roadmap
            </Typography>
          </Box>
        </Stack>
      </GlassCard>

      <Box
        sx={{
          p: { xs: 0.85, sm: 1.1 },
          borderRadius: { xs: "22px", sm: "28px" },
          background:
            "linear-gradient(145deg, rgba(255,255,255,0.98), rgba(238,245,255,0.78))",
          border: "1px solid rgba(15,36,56,0.10)",
          boxShadow: "0 24px 65px rgba(15,36,56,0.13)",
          overflow: "hidden",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            borderRadius: { xs: "18px", sm: "23px" },
            bgcolor: COLORS.white,
            border: "1px solid rgba(15,36,56,0.08)",
            overflow: "hidden",
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              px: { xs: 1.35, sm: 1.9 },
              py: 1.05,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              borderBottom: "1px solid rgba(15,36,56,0.08)",
              background: "linear-gradient(180deg,#ffffff,#f8fafc)",
              minWidth: 0,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
  <Box
    sx={{
      width: 38,
      height: 38,
      borderRadius: "13px",
      bgcolor: COLORS.white,
      display: "grid",
      placeItems: "center",
      boxShadow: "0 8px 18px rgba(15,36,56,0.10)",
      border: `1px solid ${COLORS.border}`,
      flexShrink: 0,
      overflow: "hidden",
    }}
  >
    <Box
      component="img"
      src="/images/logo_simbolo.png"
      alt="DQPa"
      sx={{
        width: 32,
        height: 32,
        objectFit: "contain",
      }}
    />
  </Box>

  <Box sx={{ minWidth: 0 }}>
    <Typography
      sx={{
        color: COLORS.blueDark,
        fontSize: "0.84rem",
        fontWeight: 950,
        lineHeight: 1.1,
        ...textNoHyphenSx,
      }}
      noWrap
    >
      DQPa Assessment
    </Typography>

    <Typography
      sx={{
        color: COLORS.muted,
        fontSize: "0.62rem",
        fontWeight: 750,
        ...textNoHyphenSx,
      }}
      noWrap
    >
      Visión ejecutiva
    </Typography>
  </Box>
</Stack>

            <Chip
              size="small"
              icon={
                <VerifiedIcon
                  sx={{
                    color: `${COLORS.orange} !important`,
                    fontSize: "16px !important",
                  }}
                />
              }
              label="UNE"
              sx={{
                height: 24,
                bgcolor: "rgba(211,125,54,0.10)",
                border: "1px solid rgba(211,125,54,0.18)",
                color: COLORS.blueDark,
                fontWeight: 900,
                flexShrink: 0,
                "& .MuiChip-label": { fontSize: "0.68rem", px: 0.7 },
              }}
            />
          </Box>

          <Box sx={{ p: { xs: 1.35, sm: 1.9, md: 2.2 }, minWidth: 0 }}>
            <Typography
              sx={{
                color: COLORS.blueDark,
                fontSize: { xs: "1.08rem", sm: "1.35rem" },
                fontWeight: 950,
                letterSpacing: "-0.04em",
                mb: 0.45,
                ...textNoHyphenSx,
              }}
            >
              Autoevaluación de madurez
            </Typography>

            <Typography
              sx={{
                color: COLORS.muted,
                lineHeight: 1.55,
                fontSize: { xs: "0.76rem", sm: "0.86rem" },
                mb: 1.35,
                ...textNoHyphenSx,
              }}
            >
              Evalúe procesos, detecte brechas y obtenga una hoja de ruta de mejora.
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gap: 1,
                mb: 1.15,
                width: "100%",
                minWidth: 0,
              }}
            >
              <MiniKpi value="22" label="Procesos" />
              <MiniKpi value="N2" label="Madurez" />
              <MiniKpi value="83" label="Mejoras" />
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  sm: "minmax(0, 0.75fr) minmax(0, 1fr)",
                },
                gap: 1.05,
                alignItems: "stretch",
                width: "100%",
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  minHeight: { xs: 130, sm: 158 },
                  p: 1.35,
                  borderRadius: "18px",
                  background: "linear-gradient(180deg,#102840,#173c60)",
                  color: COLORS.white,
                  position: "relative",
                  overflow: "hidden",
                  minWidth: 0,
                }}
              >
                <Typography
                  sx={{
                    position: "relative",
                    color: "rgba(255,255,255,0.72)",
                    fontWeight: 850,
                    fontSize: "0.67rem",
                    mb: 0.7,
                    ...textNoHyphenSx,
                  }}
                >
                  Resultado de madurez
                </Typography>

                <Typography
                  sx={{
                    position: "relative",
                    fontSize: { xs: "1.42rem", sm: "1.7rem" },
                    fontWeight: 950,
                    letterSpacing: "-0.05em",
                    lineHeight: 1,
                    ...textNoHyphenSx,
                  }}
                >
                  Nivel 2
                </Typography>

                <Typography
                  sx={{
                    position: "relative",
                    color: "rgba(255,255,255,0.82)",
                    fontWeight: 800,
                    fontSize: "0.8rem",
                    mt: 0.4,
                    mb: 1.05,
                    ...textNoHyphenSx,
                  }}
                >
                  Gestionado
                </Typography>

                <Box
                  sx={{
                    position: "relative",
                    p: 0.95,
                    borderRadius: "13px",
                    bgcolor: "rgba(255,255,255,0.08)",
                    border: "1px solid rgba(255,255,255,0.11)",
                  }}
                >
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.76)",
                      fontSize: "0.66rem",
                      lineHeight: 1.42,
                      ...textNoHyphenSx,
                    }}
                  >
                    Priorice acciones para avanzar al siguiente nivel.
                  </Typography>
                </Box>
              </Box>

              <Box
                sx={{
                  height: "100%",
                  p: 1.35,
                  borderRadius: "18px",
                  bgcolor: "#fbfcfe",
                  border: "1px solid rgba(15,36,56,0.08)",
                  minWidth: 0,
                }}
              >
                <Typography
                  sx={{
                    color: COLORS.blueDark,
                    fontWeight: 950,
                    mb: 1.05,
                    fontSize: "0.88rem",
                    ...textNoHyphenSx,
                  }}
                >
                  Ámbitos evaluados
                </Typography>

                <Stack spacing={1.02}>
                  {areas.map((area) => (
                    <Box key={area.name} sx={{ minWidth: 0 }}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        spacing={1}
                        sx={{ mb: 0.42, minWidth: 0 }}
                      >
                        <Typography
                          sx={{
                            color: COLORS.blueDark,
                            fontWeight: 850,
                            fontSize: "0.72rem",
                            ...textNoHyphenSx,
                          }}
                          noWrap
                        >
                          {area.name}
                        </Typography>

                        <Typography
                          sx={{
                            color: area.color,
                            fontWeight: 950,
                            fontSize: "0.72rem",
                            flexShrink: 0,
                            ...textNoHyphenSx,
                          }}
                        >
                          {area.value}%
                        </Typography>
                      </Stack>

                      <Box
                        sx={{
                          height: 7,
                          borderRadius: "999px",
                          bgcolor: "rgba(15,36,56,0.08)",
                          overflow: "hidden",
                        }}
                      >
                        <Box
                          sx={{
                            height: "100%",
                            width: `${area.value}%`,
                            borderRadius: "999px",
                            bgcolor: area.color,
                            transformOrigin: "left center",
                            animation: "growBar 1.15s ease both",
                          }}
                        />
                      </Box>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const MobileDashboardPreview = () => {
  const areas = [
    { name: "Gobierno", value: 76, color: COLORS.blue },
    { name: "Gestión", value: 64, color: COLORS.orange },
    { name: "Calidad", value: 58, color: COLORS.blueMid },
  ];

  return (
    <Box
      sx={{
        display: { xs: "block", md: "none" },
        width: "100%",
        maxWidth: "100%",
        mt: 0.8,
        minWidth: 0,
      }}
    >
      <CleanCard
        sx={{
          p: { xs: 1.55, sm: 1.8 },
          borderRadius: "22px",
          boxShadow: "0 14px 36px rgba(15,36,56,0.08)",
        }}
      >
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1.1} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "13px",
                bgcolor: COLORS.blueDark,
                color: COLORS.white,
                display: "grid",
                placeItems: "center",
                fontWeight: 950,
                flexShrink: 0,
              }}
            >
              D
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  color: COLORS.blueDark,
                  fontWeight: 950,
                  fontSize: "0.9rem",
                  lineHeight: 1.1,
                  ...textNoHyphenSx,
                }}
              >
                Diagnóstico DQPa
              </Typography>

              <Typography
                sx={{
                  color: COLORS.muted,
                  fontSize: "0.68rem",
                  lineHeight: 1.35,
                  ...textNoHyphenSx,
                }}
              >
                Resultado inicial de madurez
              </Typography>
            </Box>
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
              gap: 0.8,
              width: "100%",
              minWidth: 0,
            }}
          >
            {[
              { value: "22", label: "Procesos" },
              { value: "N2", label: "Madurez" },
              { value: "83", label: "Mejoras" },
            ].map((item) => (
              <Box
                key={item.label}
                sx={{
                  p: 0.95,
                  borderRadius: "14px",
                  bgcolor: COLORS.soft,
                  border: `1px solid ${COLORS.border}`,
                  minWidth: 0,
                }}
              >
                <Typography
                  sx={{
                    color: COLORS.blueDark,
                    fontWeight: 950,
                    fontSize: "0.9rem",
                    lineHeight: 1,
                    ...textNoHyphenSx,
                  }}
                >
                  {item.value}
                </Typography>

                <Typography
                  sx={{
                    color: COLORS.muted,
                    fontWeight: 800,
                    fontSize: "0.58rem",
                    mt: 0.4,
                    lineHeight: 1.2,
                    ...textNoHyphenSx,
                  }}
                >
                  {item.label}
                </Typography>
              </Box>
            ))}
          </Box>

          <Stack spacing={0.95}>
            {areas.map((area) => (
              <Box key={area.name} sx={{ minWidth: 0 }}>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.35 }}>
                  <Typography
                    sx={{
                      color: COLORS.blueDark,
                      fontWeight: 850,
                      fontSize: "0.72rem",
                      ...textNoHyphenSx,
                    }}
                  >
                    {area.name}
                  </Typography>

                  <Typography
                    sx={{
                      color: area.color,
                      fontWeight: 950,
                      fontSize: "0.72rem",
                      ...textNoHyphenSx,
                    }}
                  >
                    {area.value}%
                  </Typography>
                </Stack>

                <Box
                  sx={{
                    height: 7,
                    borderRadius: "999px",
                    bgcolor: "rgba(15,36,56,0.08)",
                    overflow: "hidden",
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      width: `${area.value}%`,
                      height: "100%",
                      borderRadius: "999px",
                      bgcolor: area.color,
                    }}
                  />
                </Box>
              </Box>
            ))}
          </Stack>
        </Stack>
      </CleanCard>
    </Box>
  );
};

const FeatureCard = ({ icon, title, desc }) => (
  <CleanCard
    sx={{
      height: "100%",
      p: { xs: 2.1, md: 2.65 },
      boxShadow: "none",
      transition: "all .22s ease",
      "&:hover": {
        transform: { xs: "none", md: "translateY(-5px)" },
        boxShadow: { xs: "none", md: "0 22px 55px rgba(15,36,56,0.08)" },
        borderColor: "rgba(211,125,54,0.24)",
      },
    }}
  >
    <IconBadge size={50}>{icon}</IconBadge>

    <Typography
      sx={{
        color: COLORS.blueDark,
        fontWeight: 950,
        fontSize: "1.02rem",
        letterSpacing: "-0.025em",
        mt: 2,
        mb: 0.7,
        ...textNoHyphenSx,
      }}
    >
      {title}
    </Typography>

    <Typography
      sx={{
        color: COLORS.muted,
        lineHeight: 1.58,
        fontSize: "0.86rem",
        ...textNoHyphenSx,
      }}
    >
      {desc}
    </Typography>
  </CleanCard>
);

const PlansSection = ({ onSelect }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: {
        xs: "minmax(0, 1fr)",
        md: "repeat(3, minmax(0, 1fr))",
      },
      gap: { xs: 2.2, md: 2.6 },
      alignItems: "stretch",
      width: "100%",
      minWidth: 0,

      "& > *": {
        minWidth: 0,
      },
    }}
  >
    {plans.map((plan, index) => {
      const isFeatured = plan.planType === "premiumPro";

      return (
        <CleanCard
          key={plan.planType}
          sx={{
            position: "relative",
            height: "100%",
            p: { xs: 2.35, md: 2.8 },
            borderRadius: "28px",
            boxShadow: isFeatured
              ? "0 26px 70px rgba(30,75,129,0.14)"
              : "0 18px 45px rgba(15,36,56,0.055)",
            borderColor: isFeatured ? "rgba(211,125,54,0.35)" : COLORS.border,
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            overflow: "hidden",
            background: isFeatured
              ? "linear-gradient(180deg,#ffffff 0%,#fffaf5 100%)"
              : COLORS.white,
            transition: "all .25s ease",

            "&:hover": {
              transform: { xs: "none", md: "translateY(-6px)" },
              boxShadow: isFeatured
                ? "0 32px 80px rgba(30,75,129,0.18)"
                : "0 26px 65px rgba(15,36,56,0.10)",
              borderColor: isFeatured
                ? "rgba(211,125,54,0.48)"
                : "rgba(30,75,129,0.18)",
            },

            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: 5,
              background: isFeatured
                ? `linear-gradient(90deg, ${COLORS.orange}, ${COLORS.blue})`
                : index === 0
                ? `linear-gradient(90deg, ${COLORS.blue}, rgba(30,75,129,0.25))`
                : `linear-gradient(90deg, ${COLORS.orange}, rgba(211,125,54,0.25))`,
            },
          }}
        >
          <Box
            sx={{
              mb: 2,
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 1.5,
              minWidth: 0,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  color: COLORS.blueDark,
                  fontSize: { xs: "1.35rem", md: "1.45rem" },
                  fontWeight: 950,
                  letterSpacing: "-0.04em",
                  lineHeight: 1.1,
                  mb: 0.9,
                  ...textNoHyphenSx,
                }}
              >
                {plan.title}
              </Typography>

              <Typography
                sx={{
                  color: COLORS.muted,
                  lineHeight: 1.58,
                  fontSize: "0.9rem",
                  ...textNoHyphenSx,
                }}
              >
                {plan.description}
              </Typography>
            </Box>

            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "16px",
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
                color: isFeatured ? COLORS.orange : COLORS.blue,
                bgcolor: isFeatured
                  ? "rgba(211,125,54,0.10)"
                  : "rgba(30,75,129,0.07)",
                border: isFeatured
                  ? "1px solid rgba(211,125,54,0.18)"
                  : "1px solid rgba(30,75,129,0.12)",
              }}
            >
              {isFeatured ? (
                <VerifiedIcon sx={{ fontSize: 24 }} />
              ) : plan.planType === "premium" ? (
                <AutoGraphIcon sx={{ fontSize: 24 }} />
              ) : (
                <AssessmentIcon sx={{ fontSize: 24 }} />
              )}
            </Box>
          </Box>

          <Divider sx={{ borderColor: COLORS.border, mb: 2.2 }} />

          <Stack spacing={1.15} sx={{ mb: 2.8, flexGrow: 1 }}>
            {plan.features.map((feature) => (
              <Stack
                key={feature.text}
                direction="row"
                spacing={1.1}
                alignItems="flex-start"
                sx={{ minWidth: 0 }}
              >
                {feature.included ? (
                  <CheckCircleIcon
                    sx={{
                      color: COLORS.orange,
                      fontSize: 19,
                      mt: "2px",
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <HighlightOffIcon
                    sx={{
                      color: "rgba(100,116,139,0.45)",
                      fontSize: 19,
                      mt: "2px",
                      flexShrink: 0,
                    }}
                  />
                )}

                <Typography
                  sx={{
                    color: feature.included
                      ? COLORS.text
                      : "rgba(100,116,139,0.68)",
                    lineHeight: 1.45,
                    fontSize: "0.86rem",
                    minWidth: 0,
                    ...textNoHyphenSx,
                  }}
                >
                  {feature.text}
                </Typography>
              </Stack>
            ))}
          </Stack>

          <Button
            variant="contained"
            fullWidth
            onClick={() => onSelect(plan.planType)}
            endIcon={isFeatured ? <ArrowForwardIcon /> : undefined}
            sx={{
              py: 1.18,
              borderRadius: "16px",
              textTransform: "none",
              fontWeight: 950,
              bgcolor: isFeatured ? COLORS.orange : COLORS.blue,
              color: COLORS.white,
              boxShadow: isFeatured
                ? "0 16px 34px rgba(211,125,54,0.22)"
                : "0 14px 30px rgba(30,75,129,0.16)",
              "&:hover": {
                bgcolor: isFeatured ? COLORS.orangeDark : COLORS.blueDark,
                boxShadow: isFeatured
                  ? "0 20px 42px rgba(211,125,54,0.30)"
                  : "0 18px 38px rgba(30,75,129,0.22)",
              },
            }}
          >
            {plan.cta}
          </Button>
        </CleanCard>
      );
    })}
  </Box>
);

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [formValues, setFormValues] = useState({
    email: "",
    message: "",
    policyAccepted: false,
  });

  const [errors, setErrors] = useState({
    email: false,
    message: false,
    policyAccepted: false,
  });

  const [isSending, setIsSending] = useState(false);

  const [submitStatus, setSubmitStatus] = useState({
    type: "",
    message: "",
  });

  const scrollToSection = (section) => {
    const runScroll = () =>
      scroller.scrollTo(section, {
        smooth: true,
        duration: 750,
        offset: -96,
      });

    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(runScroll, 400);
    } else {
      runScroll();
    }
  };

  const validateEmail = (email) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || "").trim());

  const handlePlanSelection = (planType) => {
    if (planType === "premiumPro") {
      scrollToSection("contacto");
      return;
    }

    navigate(`/register?plan=${planType}`);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: false,
      }));
    }

    if (submitStatus.message) {
      setSubmitStatus({
        type: "",
        message: "",
      });
    }
  };

  const handleCheckboxChange = (event) => {
    setFormValues((prev) => ({
      ...prev,
      policyAccepted: event.target.checked,
    }));

    if (errors.policyAccepted) {
      setErrors((prev) => ({
        ...prev,
        policyAccepted: false,
      }));
    }

    if (submitStatus.message) {
      setSubmitStatus({
        type: "",
        message: "",
      });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (isSending) return;

    const emailTrimmed = formValues.email.trim();
    const messageTrimmed = formValues.message.trim();

    const newErrors = {
      email: !validateEmail(emailTrimmed),
      message: messageTrimmed.length < 10,
      policyAccepted: !formValues.policyAccepted,
    };

    setErrors(newErrors);

    if (Object.values(newErrors).some(Boolean)) {
      setSubmitStatus({
        type: "error",
        message: "Revisa los campos del formulario antes de enviar tu consulta.",
      });
      return;
    }

    try {
      setIsSending(true);
      setSubmitStatus({ type: "", message: "" });

      await axiosInstance.post(API_ENDPOINTS.admin.contacto, {
        email: emailTrimmed,
        message: messageTrimmed,
      });

      setSubmitStatus({
        type: "success",
        message:
          "Mensaje enviado correctamente. Nuestro equipo revisará tu consulta y contactará contigo si es necesario.",
      });

      setFormValues({
        email: "",
        message: "",
        policyAccepted: false,
      });

      setErrors({
        email: false,
        message: false,
        policyAccepted: false,
      });
    } catch (error) {
      setSubmitStatus({
        type: "error",
        message:
          error.message || "No se pudo enviar el mensaje. Inténtalo de nuevo en unos minutos.",
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <>
      <GlobalStyles
        styles={{
          "*": {
            boxSizing: "border-box",
            hyphens: "none",
            WebkitHyphens: "none",
            MozHyphens: "none",
            msHyphens: "none",
          },
          html: {
            scrollBehavior: "smooth",
            overflowX: "hidden",
            width: "100%",
            maxWidth: "100%",
            fontSize: "14.5px",
            hyphens: "none",
          },
          body: {
            margin: 0,
            overflowX: "hidden",
            width: "100%",
            maxWidth: "100vw",
            minWidth: 0,
            background: COLORS.white,
            WebkitFontSmoothing: "antialiased",
            MozOsxFontSmoothing: "grayscale",
            hyphens: "none",
          },
          "#root": {
            overflowX: "clip",
            width: "100%",
            maxWidth: "100vw",
            minWidth: 0,
          },
          "main, section, header, footer": {
            width: "100%",
            maxWidth: "100vw",
            overflowX: "clip",
          },
          ".MuiContainer-root": {
            minWidth: "0 !important",
          },
          ".MuiBox-root, .MuiStack-root": {
            minWidth: 0,
          },
          ".MuiTypography-root": {
            overflowWrap: "normal",
            wordBreak: "normal",
            hyphens: "none",
          },
          ".MuiButton-root": {
            minWidth: "0 !important",
            hyphens: "none",
          },
          ".MuiChip-root": {
            maxWidth: "100%",
          },
          ".MuiChip-label": {
            minWidth: 0,
            maxWidth: "100%",
            whiteSpace: "normal",
            overflow: "visible",
            textOverflow: "clip",
            hyphens: "none",
          },
          ".MuiCard-root": {
            minWidth: 0,
            maxWidth: "100%",
          },
          img: {
            display: "block",
            maxWidth: "100%",
            height: "auto",
          },
          a: {
            overflowWrap: "normal",
            wordBreak: "normal",
            hyphens: "none",
          },
          "@keyframes floatSoft": {
            "0%": { transform: "translate3d(0,0,0)" },
            "50%": { transform: "translate3d(0,-8px,0)" },
            "100%": { transform: "translate3d(0,0,0)" },
          },
          "@keyframes logosMarquee": {
            "0%": { transform: "translate3d(0,0,0)" },
            "100%": { transform: "translate3d(-50%,0,0)" },
          },
          "@keyframes growBar": {
            "0%": { transform: "scaleX(0)" },
            "100%": { transform: "scaleX(1)" },
          },
          "@media (max-width: 600px)": {
            html: {
              fontSize: "14px",
            },
            "h1, h2, h3, p, span": {
              maxWidth: "100%",
              hyphens: "none",
            },
          },
        }}
      />

      <Element name="inicio">
  <Box
    component="section"
    sx={{
      position: "relative",
      overflow: "hidden",

      mt: 0,

      pt: {
        xs: "112px",
        sm: "120px",
        md: "128px",
        lg: "136px",
      },

      pb: {
        xs: 5.5,
        md: 7.5,
      },

      background:
        "radial-gradient(circle at 78% 18%, rgba(211,125,54,0.12), transparent 32%), radial-gradient(circle at 16% 24%, rgba(30,75,129,0.10), transparent 30%), linear-gradient(180deg,#ffffff 0%,#f8fafc 100%)",
    }}
  >
    <Box
      sx={{
        position: "absolute",
        left: 0,
        right: 0,
        top: 0,
        bottom: 0,
        opacity: 0.55,
        backgroundImage:
          "linear-gradient(rgba(15,36,56,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(15,36,56,0.03) 1px, transparent 1px)",
        backgroundSize: "54px 54px",
        pointerEvents: "none",
      }}
    />

    <PageContainer sx={{ position: "relative", zIndex: 1 }}>
      <ResponsiveGrid
        columns={{
          xs: "minmax(0, 1fr)",
          md: "minmax(0, 1fr) minmax(0, 0.88fr)",
        }}
        gap={{ xs: 3.5, md: 4, lg: 5 }}
        sx={{
          alignItems: "center",
          overflow: "visible",
          "& > *": {
            overflow: "visible",
          },
        }}
      >
        <SafeBox sx={{ overflow: "visible" }}>
          <Stack spacing={{ xs: 2.1, md: 2.5 }} sx={{ minWidth: 0 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
              useFlexGap
              sx={{ maxWidth: "100%", minWidth: 0 }}
            >
              <Chip
                icon={<VerifiedIcon sx={{ color: `${COLORS.orange} !important` }} />}
                label="Alineado con UNE 0077 · 0078 · 0079 · 0080"
                sx={{
                  height: "auto",
                  minHeight: { xs: 32, sm: 38 },
                  borderRadius: "13px",
                  bgcolor: COLORS.white,
                  border: `1px solid ${COLORS.border}`,
                  color: COLORS.blueDark,
                  fontWeight: 900,
                  boxShadow: "0 12px 30px rgba(15,36,56,0.055)",
                  maxWidth: "100%",
                  "& .MuiChip-label": {
                    fontSize: { xs: "0.62rem", sm: "0.78rem" },
                    whiteSpace: "normal",
                    lineHeight: 1.25,
                    py: { xs: 0.6, sm: 0 },
                  },
                }}
              />

              <Chip
                icon={<GroupsIcon sx={{ color: `${COLORS.blue} !important` }} />}
                label="Para todas las organizaciones"
                sx={{
                  display: { xs: "none", sm: "inline-flex" },
                  height: 38,
                  borderRadius: "13px",
                  bgcolor: "rgba(30,75,129,0.07)",
                  border: "1px solid rgba(30,75,129,0.10)",
                  color: COLORS.blueDark,
                  fontWeight: 900,
                }}
              />
            </Stack>

            <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
              <Typography
                component="h1"
                sx={{
                  fontSize: {
                    xs: "clamp(1.55rem, 8vw, 2rem)",
                    sm: "2rem",
                    md: "2.45rem",
                    lg: "3rem",
                  },
                  lineHeight: { xs: 1.16, md: 1.06 },
                  letterSpacing: { xs: "-0.015em", md: "-0.05em" },
                  fontWeight: 950,
                  color: COLORS.blueDark,
                  width: "100%",
                  maxWidth: "100%",
                  ...textNoHyphenSx,
                }}
              >
                Conozca su nivel en{" "}
                <Box component="span" sx={{ color: COLORS.orange, ...textNoHyphenSx }}>
                  gobierno y gestión de datos
                </Box>
              </Typography>

              <Typography
                sx={{
                  color: COLORS.text,
                  fontSize: { xs: "0.84rem", sm: "0.9rem", md: "0.96rem" },
                  lineHeight: 1.6,
                  maxWidth: 590,
                  mt: 1.5,
                  ...textNoHyphenSx,
                }}
              >
                DQPa ayuda a medir la capacidad y madurez de sus procesos de
                gobierno, gestión y calidad del dato, generando diagnóstico,
                brechas y propuestas de mejora priorizadas.
              </Typography>
            </Box>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.25}
              sx={{
                width: "100%",
                maxWidth: { xs: "100%", sm: "fit-content" },
              }}
            >
              <PrimaryButton
                variant="contained"
                onClick={() => navigate("/evaluacion")}
                endIcon={<ArrowForwardIcon />}
              >
                <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
                  Comenzar evaluación gratuita
                </Box>
                <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
                  Comenzar evaluación
                </Box>
              </PrimaryButton>

              <SecondaryButton variant="outlined" onClick={() => scrollToSection("contacto")}>
                Contactar
              </SecondaryButton>
            </Stack>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  sm: "repeat(3, minmax(0, 1fr))",
                },
                gap: 1.2,
                width: "100%",
                maxWidth: 680,
                minWidth: 0,
              }}
            >
              {[
                { icon: <FactCheckIcon />, title: "Evaluación" },
                { icon: <AutoGraphIcon />, title: "Roadmap" },
                { icon: <ShieldOutlinedIcon />, title: "Criterio UNE" },
              ].map((item) => (
                <Stack
                  key={item.title}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{
                    p: 1.2,
                    borderRadius: "16px",
                    bgcolor: "rgba(255,255,255,0.74)",
                    border: `1px solid ${COLORS.border}`,
                    minWidth: 0,
                    maxWidth: "100%",
                  }}
                >
                  <Box
                    sx={{
                      color: COLORS.orange,
                      display: "flex",
                      flexShrink: 0,
                      "& svg": { fontSize: 20 },
                    }}
                  >
                    {item.icon}
                  </Box>

                  <Typography
                    sx={{
                      color: COLORS.blueDark,
                      fontWeight: 900,
                      fontSize: "0.84rem",
                      lineHeight: 1.25,
                      ...textNoHyphenSx,
                    }}
                  >
                    {item.title}
                  </Typography>
                </Stack>
              ))}
            </Box>

            <MobileDashboardPreview />
          </Stack>
        </SafeBox>

        <SafeBox
          sx={{
            display: { xs: "none", md: "block" },
            overflow: "visible",
          }}
        >
          <Box
            sx={{
              animation: "floatSoft 7s ease-in-out infinite",
              width: "100%",
              maxWidth: 500,
              mx: "auto",
              minWidth: 0,
              overflow: "visible",
            }}
          >
            <DashboardPreview />
          </Box>
        </SafeBox>
      </ResponsiveGrid>
    </PageContainer>
  </Box>
</Element>

      <Box
        sx={{
          py: { xs: 3.6, md: 4.8 },
          bgcolor: COLORS.white,
          borderTop: `1px solid ${COLORS.border}`,
          borderBottom: `1px solid ${COLORS.border}`,
          overflow: "hidden",
        }}
      >
        <PageContainer>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2.2}
            alignItems={{ xs: "flex-start", md: "center" }}
            justifyContent="space-between"
            sx={{ mb: 3, minWidth: 0 }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  color: COLORS.blueDark,
                  fontSize: { xs: "1.2rem", md: "1.5rem" },
                  fontWeight: 950,
                  letterSpacing: "-0.035em",
                  ...textNoHyphenSx,
                }}
              >
                Marco de referencia para evaluar procesos de datos
              </Typography>

              <Typography
                sx={{
                  color: COLORS.muted,
                  mt: 0.6,
                  fontSize: "0.88rem",
                  ...textNoHyphenSx,
                }}
              >
                Gobierno, gestión, calidad y madurez organizacional.
              </Typography>
            </Box>

            <Chip
              icon={<VerifiedIcon sx={{ color: `${COLORS.orange} !important` }} />}
              label="UNE 0077 · UNE 0078 · UNE 0079 · UNE 0080:2023"
              sx={{
                height: "auto",
                minHeight: 32,
                bgcolor: "rgba(211,125,54,0.09)",
                border: "1px solid rgba(211,125,54,0.18)",
                color: COLORS.blueDark,
                fontWeight: 900,
                maxWidth: "100%",
                "& .MuiChip-label": {
                  whiteSpace: "normal",
                  lineHeight: 1.25,
                  py: { xs: 0.65, sm: 0 },
                  fontSize: { xs: "0.7rem", sm: "0.8rem" },
                },
              }}
            />
          </Stack>

          <Box
            sx={{
              overflow: "hidden",
              position: "relative",
              width: "100%",
              maxWidth: "100%",
              "&::before": {
                content: '""',
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: { xs: 28, md: 100 },
                zIndex: 2,
                background: "linear-gradient(90deg,#fff,rgba(255,255,255,0))",
              },
              "&::after": {
                content: '""',
                position: "absolute",
                right: 0,
                top: 0,
                bottom: 0,
                width: { xs: 28, md: 100 },
                zIndex: 2,
                background: "linear-gradient(270deg,#fff,rgba(255,255,255,0))",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                width: "fit-content",
                maxWidth: "none",
                animation: "logosMarquee 32s linear infinite",
                willChange: "transform",
                "&:hover": { animationPlayState: "paused" },
              }}
            >
              {[...standardLogos, ...standardLogos].map((item, index) => (
                <Box
                  key={`${item.name}-${index}`}
                  sx={{
                    width: { xs: 135, sm: 170, md: 195 },
                    height: { xs: 105, sm: 135, md: 150 },
                    mx: { xs: 1.1, sm: 1.8, md: 3 },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Box
                    component="img"
                    src={item.image}
                    alt={item.name}
                    sx={{
                      height: "100%",
                      width: "auto",
                      objectFit: "contain",
                      transition: "transform .2s ease",
                      "&:hover": { transform: "scale(1.04)" },
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        </PageContainer>
      </Box>

      <Element name="solucion">
        <Box component="section" sx={{ py: { xs: 6, md: 10 }, bgcolor: COLORS.white, overflow: "hidden" }}>
          <PageContainer>
            <ResponsiveGrid
              columns={{
                xs: "minmax(0, 1fr)",
                md: "minmax(0, 0.9fr) minmax(0, 1.1fr)",
              }}
              gap={{ xs: 4, md: 6 }}
              sx={{ alignItems: "center" }}
            >
              <SafeBox>
                <SectionHeader
                  align="left"
                  eyebrow="Herramienta"
                  title="Del cuestionario al"
                  highlight="plan de mejora"
                  subtitle="La plataforma transforma respuestas de autoevaluación en indicadores comprensibles, brechas detectadas y acciones concretas para avanzar en madurez."
                />

                <Stack spacing={1.35}>
                  {[
                    "Evalúe procesos de datos de forma guiada y comparable.",
                    "Obtenga resultados visuales por ámbito y nivel.",
                    "Priorice iniciativas según impacto y necesidad.",
                  ].map((item) => (
                    <Stack key={item} direction="row" spacing={1.2} alignItems="flex-start">
                      <CheckCircleIcon sx={{ color: COLORS.orange, fontSize: 21, mt: "2px", flexShrink: 0 }} />

                      <Typography sx={{ color: COLORS.text, lineHeight: 1.6, fontSize: "0.9rem", ...textNoHyphenSx }}>
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </SafeBox>

              <SafeBox>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "minmax(0, 1fr)",
                      sm: "repeat(2, minmax(0, 1fr))",
                    },
                    gap: 1.8,
                    width: "100%",
                    minWidth: 0,
                  }}
                >
                  {[
                    {
                      step: "01",
                      title: "Evaluar",
                      desc: "Cuestionarios por proceso y nivel.",
                      icon: <AssessmentIcon />,
                    },
                    {
                      step: "02",
                      title: "Diagnosticar",
                      desc: "Capacidad, madurez y brechas.",
                      icon: <BarChartIcon />,
                    },
                    {
                      step: "03",
                      title: "Priorizar",
                      desc: "Acciones ordenadas por impacto.",
                      icon: <TrackChangesIcon />,
                    },
                    {
                      step: "04",
                      title: "Presentar",
                      desc: "Resultados para dirección y equipos.",
                      icon: <DescriptionIcon />,
                    },
                  ].map((item) => (
                    <CleanCard key={item.step} sx={{ p: 2.15, height: "100%", boxShadow: "none" }}>
                      <Stack direction="row" spacing={1.35} alignItems="flex-start" sx={{ minWidth: 0 }}>
                        <IconBadge size={46}>{item.icon}</IconBadge>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              color: "rgba(211,125,54,0.70)",
                              fontWeight: 950,
                              fontSize: "0.72rem",
                              mb: 0.2,
                              ...textNoHyphenSx,
                            }}
                          >
                            PASO {item.step}
                          </Typography>

                          <Typography sx={{ color: COLORS.blueDark, fontWeight: 950, fontSize: "1rem", ...textNoHyphenSx }}>
                            {item.title}
                          </Typography>

                          <Typography sx={{ color: COLORS.muted, lineHeight: 1.5, mt: 0.45, fontSize: "0.86rem", ...textNoHyphenSx }}>
                            {item.desc}
                          </Typography>
                        </Box>
                      </Stack>
                    </CleanCard>
                  ))}
                </Box>
              </SafeBox>
            </ResponsiveGrid>
          </PageContainer>
        </Box>
      </Element>

      <Element name="funcionalidades">
        <Box component="section" sx={{ py: { xs: 6, md: 10 }, bgcolor: COLORS.soft, overflow: "hidden" }}>
          <PageContainer>
            <SectionHeader
              eyebrow="Funcionalidades"
              title="Todo lo necesario para evaluar con"
              highlight="criterio"
              subtitle="Diseñada para responsables de datos, calidad, tecnología y dirección que necesitan claridad, trazabilidad y foco en la mejora."
            />

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  sm: "repeat(2, minmax(0, 1fr))",
                  md: "repeat(3, minmax(0, 1fr))",
                },
                gap: 2.2,
                width: "100%",
                minWidth: 0,

                "& > *": {
                  minWidth: 0,
                },
              }}
            >
              {features.map((item) => (
                <FeatureCard key={item.title} {...item} />
              ))}
            </Box>
          </PageContainer>
        </Box>
      </Element>

      <Element name="beneficios">
        <Box component="section" sx={{ py: { xs: 6, md: 10 }, bgcolor: COLORS.white, overflow: "hidden" }}>
          <PageContainer>
            <ResponsiveGrid
              columns={{
                xs: "minmax(0, 1fr)",
                md: "minmax(0, 1fr) minmax(0, 1fr)",
              }}
              gap={{ xs: 4, md: 5 }}
              sx={{ alignItems: "center" }}
            >
              <SafeBox>
                <GlassCard
                  sx={{
                    p: { xs: 2.25, md: 3 },
                    borderRadius: "28px",
                    background: "linear-gradient(145deg,#102840,#173b5f)",
                    color: COLORS.white,
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 950,
                      fontSize: { xs: "1.45rem", md: "1.85rem" },
                      letterSpacing: "-0.04em",
                      mb: 1,
                      ...textNoHyphenSx,
                    }}
                  >
                    Una visión ejecutiva, no solo un cuestionario
                  </Typography>

                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.76)",
                      lineHeight: 1.6,
                      mb: 2.6,
                      fontSize: "0.9rem",
                      ...textNoHyphenSx,
                    }}
                  >
                    DQPa convierte la autoevaluación en una herramienta de decisión:
                    estado actual, brechas, prioridades y evolución.
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "minmax(0, 1fr)",
                        sm: "repeat(2, minmax(0, 1fr))",
                      },
                      gap: 1.3,
                    }}
                  >
                    {[
                      { value: "As Is", label: "Estado actual" },
                      { value: "Gap", label: "Brechas" },
                      { value: "Roadmap", label: "Mejora" },
                      { value: "UNE", label: "Criterio" },
                    ].map((item) => (
                      <Box
                        key={item.label}
                        sx={{
                          p: 1.5,
                          borderRadius: "17px",
                          bgcolor: "rgba(255,255,255,0.08)",
                          border: "1px solid rgba(255,255,255,0.12)",
                          minWidth: 0,
                        }}
                      >
                        <Typography sx={{ fontSize: "1.08rem", fontWeight: 950, ...textNoHyphenSx }}>
                          {item.value}
                        </Typography>

                        <Typography sx={{ color: "rgba(255,255,255,0.68)", fontSize: "0.74rem", fontWeight: 800, ...textNoHyphenSx }}>
                          {item.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </GlassCard>
              </SafeBox>

              <SafeBox>
                <SectionHeader
                  align="left"
                  eyebrow="Beneficios"
                  title="Más claridad para decidir"
                  highlight="qué mejorar primero"
                  subtitle="Ayuda a justificar iniciativas de datos con una lectura comprensible para perfiles técnicos y no técnicos."
                />

                <Stack spacing={1.25}>
                  {[
                    "Alinea a negocio, tecnología y responsables de datos con una visión común.",
                    "Reduce la incertidumbre sobre qué procesos necesitan atención prioritaria.",
                    "Facilita la comunicación de resultados a comités y dirección.",
                    "Convierte la evaluación en acciones concretas y medibles.",
                  ].map((item) => (
                    <Stack key={item} direction="row" spacing={1.2} alignItems="flex-start">
                      <CheckCircleIcon sx={{ color: COLORS.orange, fontSize: 21, mt: "2px", flexShrink: 0 }} />

                      <Typography sx={{ color: COLORS.text, lineHeight: 1.6, fontSize: "0.9rem", ...textNoHyphenSx }}>
                        {item}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </SafeBox>
            </ResponsiveGrid>
          </PageContainer>
        </Box>
      </Element>

      <Element name="planes">
        <Box component="section" sx={{ py: { xs: 6, md: 10 }, bgcolor: COLORS.soft, overflow: "hidden" }}>
          <PageContainer>
            <SectionHeader
              eyebrow="Planes"
              title="Elija el alcance adecuado para su"
              highlight="organización"
              subtitle="Empiece con una evaluación inicial o amplíe el alcance según el nivel de profundidad que necesite su organización."
            />

            <PlansSection onSelect={handlePlanSelection} />
          </PageContainer>
        </Box>
      </Element>

      <Element name="contacto">
        <Box
          component="section"
          sx={{
            py: { xs: 6, md: 10 },
            bgcolor: COLORS.white,
            background:
              "radial-gradient(circle at 18% 15%, rgba(30,75,129,0.08), transparent 28%), radial-gradient(circle at 85% 20%, rgba(211,125,54,0.10), transparent 28%), #ffffff",
            overflow: "hidden",
          }}
        >
          <PageContainer>
            <ResponsiveGrid
              columns={{
                xs: "minmax(0, 1fr)",
                md: "minmax(0, 0.8fr) minmax(0, 1fr)",
              }}
              gap={{ xs: 4, md: 5 }}
              sx={{ alignItems: "stretch" }}
            >
              <SafeBox>
                <SectionHeader
                  align="left"
                  eyebrow="Contacto"
                  title="Hablemos sobre sus necesidades de"
                  highlight="evaluación"
                  subtitle="Solicite orientación y le ayudaremos a elegir el mejor alcance de evaluación para su organización."
                />

                <Stack spacing={1.45} sx={{ mb: 2.5 }}>
                  {contactItems.map((item) => (
                    <CleanCard key={item.title} sx={{ p: 1.8, boxShadow: "none", borderRadius: "22px" }}>
                      <Stack direction="row" spacing={1.35} alignItems="center" sx={{ minWidth: 0 }}>
                        <IconBadge size={46} variant="blue">
                          {item.icon}
                        </IconBadge>

                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ color: COLORS.blueDark, fontWeight: 950, ...textNoHyphenSx }}>
                            {item.title}
                          </Typography>

                          <Typography sx={{ color: COLORS.muted, ...textNoHyphenSx }}>
                            {item.value}
                          </Typography>
                        </Box>
                      </Stack>
                    </CleanCard>
                  ))}
                </Stack>

                <Stack direction="row" spacing={1.2}>
                  <IconButton
                    component="a"
                    href="https://www.linkedin.com/company/dqteam-sl/posts/?feedView=all"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="LinkedIn de DQTeam"
                    sx={{
                      width: 46,
                      height: 46,
                      color: COLORS.blue,
                      bgcolor: "rgba(30,75,129,0.06)",
                      border: "1px solid rgba(30,75,129,0.10)",
                      "&:hover": {
                        color: COLORS.orange,
                        bgcolor: "rgba(211,125,54,0.08)",
                      },
                    }}
                  >
                    <LinkedInIcon />
                  </IconButton>

                  <IconButton
                    component="a"
                    href="https://dqteam.es"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Página web de DQTeam"
                    sx={{
                      width: 46,
                      height: 46,
                      color: COLORS.blue,
                      bgcolor: "rgba(30,75,129,0.06)",
                      border: "1px solid rgba(30,75,129,0.10)",
                      "&:hover": {
                        color: COLORS.orange,
                        bgcolor: "rgba(211,125,54,0.08)",
                      },
                    }}
                  >
                    <LanguageIcon />
                  </IconButton>
                </Stack>
              </SafeBox>

              <SafeBox>
                <CleanCard
                  component="form"
                  onSubmit={handleSubmit}
                  noValidate
                  sx={{
                    height: "100%",
                    p: { xs: 2.2, md: 3.5 },
                    borderRadius: "28px",
                    boxShadow: "none",
                  }}
                >
                  <Typography
                    sx={{
                      color: COLORS.blueDark,
                      fontSize: { xs: "1.2rem", md: "1.5rem" },
                      fontWeight: 950,
                      mb: 0.9,
                      letterSpacing: "-0.03em",
                      ...textNoHyphenSx,
                    }}
                  >
                    Queremos saber más sobre sus necesidades
                  </Typography>

                  <Typography
                    sx={{
                      color: COLORS.muted,
                      lineHeight: 1.6,
                      mb: 2.5,
                      fontSize: "0.9rem",
                      ...textNoHyphenSx,
                    }}
                  >
                    Envíenos su consulta y le ayudaremos a resolver cualquier duda sobre
                    la plataforma, los planes o el proceso de autoevaluación.
                  </Typography>

                  <Stack spacing={2}>
                    <TextField
                      label="Correo electrónico"
                      name="email"
                      type="email"
                      value={formValues.email}
                      onChange={handleInputChange}
                      fullWidth
                      variant="outlined"
                      error={errors.email}
                      disabled={isSending}
                      helperText={errors.email ? "Introduce un correo electrónico válido" : " "}
                      sx={fieldSx}
                    />

                    <TextField
                      label="Escribe aquí tu mensaje"
                      name="message"
                      value={formValues.message}
                      onChange={handleInputChange}
                      fullWidth
                      variant="outlined"
                      multiline
                      rows={5}
                      error={errors.message}
                      disabled={isSending}
                      helperText={
                        errors.message ? "El mensaje debe tener al menos 10 caracteres" : " "
                      }
                      sx={fieldSx}
                    />

                    <Box>
                      <FormControlLabel
                        sx={{ alignItems: "flex-start", m: 0, color: COLORS.muted }}
                        control={
                          <Checkbox
                            checked={formValues.policyAccepted}
                            onChange={handleCheckboxChange}
                            name="policyAccepted"
                            disabled={isSending}
                            sx={{
                              color: errors.policyAccepted ? "#d32f2f" : COLORS.blue,
                              pt: 0,
                              "&.Mui-checked": { color: COLORS.orange },
                            }}
                          />
                        }
                        label={
                          <Typography
                            sx={{
                              color: COLORS.muted,
                              lineHeight: 1.55,
                              fontSize: "0.86rem",
                              ...textNoHyphenSx,
                            }}
                          >
                            He leído y acepto la{" "}
                            <MuiLink
                              component={RouterLink}
                              to="/politica"
                              sx={{
                                color: COLORS.orange,
                                fontWeight: 850,
                                textDecoration: "none",
                                "&:hover": { textDecoration: "underline" },
                              }}
                            >
                              política de privacidad
                            </MuiLink>{" "}
                            *
                          </Typography>
                        }
                      />

                      {errors.policyAccepted && (
                        <Typography
                          sx={{
                            color: "#d32f2f",
                            mt: 0.7,
                            ml: { xs: 0, sm: 4.4 },
                            fontSize: "0.8rem",
                            ...textNoHyphenSx,
                          }}
                        >
                          Debes aceptar la política de privacidad.
                        </Typography>
                      )}
                    </Box>

                    {submitStatus.message && (
                      <Box
                        aria-live="polite"
                        sx={{
                          p: 1.6,
                          borderRadius: "16px",
                          bgcolor:
                            submitStatus.type === "success"
                              ? "rgba(34,197,94,0.08)"
                              : "rgba(211,47,47,0.08)",
                          border:
                            submitStatus.type === "success"
                              ? "1px solid rgba(34,197,94,0.18)"
                              : "1px solid rgba(211,47,47,0.18)",
                        }}
                      >
                        <Typography
                          sx={{
                            color: submitStatus.type === "success" ? "#166534" : "#b91c1c",
                            fontWeight: 800,
                            fontSize: "0.86rem",
                            lineHeight: 1.45,
                            ...textNoHyphenSx,
                          }}
                        >
                          {submitStatus.message}
                        </Typography>
                      </Box>
                    )}

                    <PrimaryButton
                      type="submit"
                      variant="contained"
                      fullWidth
                      disabled={isSending}
                      sx={{
                        mt: 0.6,
                        py: 1.35,
                        borderRadius: "16px",
                        fontSize: "0.92rem",
                        opacity: isSending ? 0.72 : 1,
                      }}
                    >
                      {isSending ? "Enviando..." : "Enviar consulta"}
                    </PrimaryButton>
                  </Stack>
                </CleanCard>
              </SafeBox>
            </ResponsiveGrid>
          </PageContainer>
        </Box>
      </Element>
    </>
  );
};

export default Home;